import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { LogOut, LayoutDashboard, PlusCircle, Compass, MessageSquare, User, Users, ShieldCheck, CreditCard, FileCheck, Wallet, Gift, CalendarDays, PackageOpen, MailWarning, Trophy, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogoMark, Wordmark } from "@/components/Logo";
import { useNotificationSocket } from "@/hooks/useNotificationSocket";

export default function AppShell() {
  const { user, loading, logout, refresh } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  useNotificationSocket(!!user);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-zinc-500 text-xs uppercase tracking-widest animate-pulse">Loading…</div>
      </div>
    );
  }
  if (!user) {
    // remember where they were headed so login returns them to the bookmark
    const next = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  const isDriver = user.role === "driver";
  const isShipper = user.role === "shipper";
  const isAdmin = user.role === "admin";

  const items = [
    isShipper && { to: "/app", icon: LayoutDashboard, label: "My requests", end: true },
    isShipper && { to: "/app/new", icon: PlusCircle, label: "New request" },
    isDriver && { to: "/app/driver", icon: Compass, label: "Browse jobs", end: true },
    isDriver && { to: "/app/bids", icon: MessageSquare, label: "My bids" },
    isDriver && { to: "/app/calendar", icon: CalendarDays, label: "Calendar" },
    isDriver && { to: "/app/payout", icon: Wallet, label: "Earnings" },
    isDriver && { to: "/app/kyc", icon: ShieldCheck, label: "Verify" },
    isShipper && { to: "/app/kyc", icon: ShieldCheck, label: "Verify" },
    isDriver && { to: "/app/refer", icon: Gift, label: "Refer" },
    isDriver && { to: "/app/billing", icon: CreditCard, label: "Pro" },
    isAdmin && { to: "/app/admin/kyc", icon: FileCheck, label: "KYC review", end: true },
    isAdmin && { to: "/app/admin/users", icon: Users, label: "User management" },
    isAdmin && { to: "/app/admin/requests", icon: PackageOpen, label: "Shipper requests" },
    isAdmin && { to: "/app/admin/email", icon: MailWarning, label: "Email log" },
    isAdmin && { to: "/app/admin/referrals", icon: Trophy, label: "Referrals" },
    isAdmin && { to: "/app/admin/sso", icon: KeyRound, label: "SSO audit" },
    { to: "/app/profile", icon: User, label: "Profile" },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col" data-testid="app-shell">
      <header className="border-b border-white/[0.06] bg-black/60 backdrop-blur sticky top-0 z-20">
        <div className="px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="app-brand">
            <LogoMark size={32} />
            <Wordmark className="text-lg hidden sm:inline" />
          </Link>
          <nav className="hidden md:flex items-center gap-1" data-testid="app-nav">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`nav-${it.label.replace(/\s/g, "-").toLowerCase()}`}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold transition-colors border ${
                    isActive
                      ? "border-[#FF3B30] text-white bg-[#FF3B30]/10"
                      : "border-transparent text-zinc-400 hover:text-white hover:border-white/10"
                  }`
                }
              >
                <it.icon className="w-3.5 h-3.5" />
                {it.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <div className="text-xs font-semibold">{user.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#FF3B30] font-bold">{user.role}</div>
            </div>
            <button
              onClick={() => {
                logout();
                nav("/", { replace: true });
              }}
              data-testid="logout-btn"
              className="inline-flex items-center gap-2 border border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30] px-3 py-2 text-xs uppercase tracking-widest font-bold transition-colors"
              aria-label="Sign out"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Sign out</span>
            </button>
          </div>
        </div>
        {/* Mobile nav */}
        <nav className="md:hidden overflow-x-auto flex gap-1 px-4 pb-3 border-t border-white/[0.06]">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                `shrink-0 inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold transition-colors border ${
                  isActive
                    ? "border-[#FF3B30] text-white bg-[#FF3B30]/10"
                    : "border-transparent text-zinc-400"
                }`
              }
            >
              <it.icon className="w-3.5 h-3.5" />
              {it.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <SmsSyncBanner user={user} onDone={refresh} />
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}

// One-time prompt for existing users (pre-consent-checkbox signups) to confirm
// text alerts. Dismissal and opt-in both persist server-side; consent stamps
// timestamp + IP via PATCH /users/me.
function SmsSyncBanner({ user, onDone }) {
  const [busy, setBusy] = useState(false);
  if (!user || user.role === "admin" || user.sms_enabled || user.sms_notice_dismissed) return null;

  const act = async (enable) => {
    setBusy(true);
    try {
      await api.patch("/users/me", enable ? { sms_enabled: true } : { sms_notice_dismissed: true });
      if (enable && user.role === "driver") {
        await api.patch("/drivers/me", { sms_enabled: true }); // bid-win texts live on the driver profile
      }
      if (enable) {
        toast.success(user.phone
          ? "Text alerts are on. Reply STOP anytime to opt out."
          : "Text alerts are on — add your phone number on Profile to receive them.");
      }
      await onDone();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-[#FF3B30]/30 bg-[#FF3B30]/[0.06]" data-testid="sms-sync-banner">
      <div className="px-6 md:px-10 py-3 flex items-center gap-4 flex-wrap">
        <MessageSquare className="w-4 h-4 text-[#FF3B30] shrink-0" />
        <p className="text-xs text-zinc-300 flex-1 min-w-[220px] leading-relaxed">
          New: text alerts for bids, haul updates and weekly lane digests. Optional — message &amp; data rates may apply, reply STOP to opt out anytime.
        </p>
        <button
          onClick={() => act(true)}
          disabled={busy}
          data-testid="sms-sync-enable"
          className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#ff5044] disabled:opacity-60 text-white px-4 py-2 text-[11px] uppercase tracking-widest font-bold transition-colors"
        >
          Turn on texts
        </button>
        <button
          onClick={() => act(false)}
          disabled={busy}
          data-testid="sms-sync-dismiss"
          className="inline-flex items-center gap-2 border border-white/10 hover:border-white/40 text-zinc-400 hover:text-white px-4 py-2 text-[11px] uppercase tracking-widest font-bold transition-colors"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
